import { useState, useCallback, useEffect } from 'react';
import { uploadFile } from '@/network/network';
import { toast } from 'sonner';
import { FormattedFile } from '@/react/components/entityViews/files/context/EntityFileContext';
import { formatFile } from '@/modern/utils';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';
import { EntityRecord } from '@/types/entity';

export const useSwivuFiles = ({ entityRecord }: { entityRecord: EntityRecord }) => {
  const { project, user } = useSwivuBryntumContext();
  const [boardFiles, setBoardFiles] = useState<FormattedFile[]>([]);

  const refreshBoardFiles = useCallback(() => {
    const files = project.boardFilesStore.query((item) => item[entityRecord.entityType] === entityRecord.id);
    setBoardFiles(formatFile(files, user));
  }, [project, entityRecord.entityType, entityRecord.id, user]);


  const uploadNewFile = useCallback(
    async (uploadedFiles: FileList | File[]) => {
      const fileArray = Array.from(uploadedFiles);
      const fileCount = fileArray.length;
      const fileText = fileCount === 1 ? 'file' : 'files';

      try {
        toast.loading(`Uploading ${fileCount} ${fileText}...`, {
          id: 'file-upload'
        });

        const uploadPromises = fileArray.map(async (file) => {
          const { data: uploadData } = await uploadFile(file, file.name);
          return uploadData.id;
        });

        const documentIds = await Promise.all(uploadPromises);
        const records = documentIds.map((documentId) => ({
          documentId: documentId,
          [entityRecord.entityType]: entityRecord.id
        }));

        project.boardFilesStore.add(records);
        await project.sync();
        toast.success(`Successfully uploaded ${fileCount} ${fileText}`, {
          id: 'file-upload'
        });
      } catch (error) {
        console.error('File upload error:', error);
        toast.error(`Failed to upload ${fileText}. Please try again.`, {
          id: 'file-upload'
        });
      }
    },
    [entityRecord, project]
  );

  const deleteFile = useCallback((id: string | number) => {
    project.boardFilesStore.remove(id);
  }, [project]);

  useEffect(() => {
    refreshBoardFiles();
  }, [refreshBoardFiles]);

  return {
    boardFiles,
    uploadNewFile,
    deleteFile,
    refreshBoardFiles
  };
};